import { useEffect, useState, type FormEvent } from 'react';
import type {
  ReviewOrganization,
  HouseholdContract,
  SaveHouseholdRequest,
} from '@urbanik/api-client';
import { CanonicalCard, TextField } from '@urbanik/ui';
import { reviewApiBase } from './api-base';
const memberId = '30000000-0000-4000-8000-000000000001';
async function call<T>(organizationId: string, init?: RequestInit) {
  const response = await fetch(
    `${reviewApiBase}/identity/multi-review/organizations/${organizationId}/members/${memberId}/households`,
    {
      ...init,
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'x-urbanik-review': '1',
      },
    },
  );
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.message ?? 'No pudimos completar la solicitud.');
  return body as T;
}
export function EntityHouseholdReviewPanel({ organization }: { organization: ReviewOrganization }) {
  const [rows, setRows] = useState<HouseholdContract[]>([]),
    [loading, setLoading] = useState(true),
    [error, setError] = useState(''),
    [success, setSuccess] = useState(''),
    [periodStart, setPeriodStart] = useState('2097-01-01'),
    [reason, setReason] = useState('Declaración ficticia para validar aislamiento por entidad'),
    [givenNames, setGivenNames] = useState('FAMILIAR'),
    [familyNames, setFamilyNames] = useState(organization.code),
    [saving, setSaving] = useState(false);
  const load = () => {
    setLoading(true);
    setError('');
    void call<{ households: HouseholdContract[] }>(organization.id)
      .then((result) => setRows(result.households))
      .catch((failure) =>
        setError(failure instanceof Error ? failure.message : 'Error inesperado.'),
      )
      .finally(() => setLoading(false));
  };
  useEffect(load, [organization.id]);
  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    const current = rows[0] ?? null;
    const input: SaveHouseholdRequest = {
      householdId: current?.id ?? null,
      expectedVersion: current?.version ?? null,
      periodStart,
      periodEnd: null,
      includesMember: true,
      members: [
        {
          id: null,
          givenNames,
          familyNames,
          relationshipCode: 'other',
          sexCode: null,
          birthDate: null,
          civilStatusCode: null,
          educationLevelCode: null,
          isCurrentlyStudying: null,
          educationInstitutionTypeCode: null,
          isPrimaryNucleus: true,
        },
      ],
      reason,
    };
    try {
      const result = await call<{ households: HouseholdContract[] }>(organization.id, {
        method: 'PUT',
        body: JSON.stringify(input),
      });
      setRows(result.households);
      setSuccess(`Hogar guardado solo en ${organization.code}. La otra entidad no fue modificada.`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Error inesperado.');
    } finally {
      setSaving(false);
    }
  }
  return (
    <section aria-label={'Hogar de ' + organization.code}>
      <h2>Hogar por entidad</h2>
      <p>
        La identidad de la persona es compartida; esta declaración familiar pertenece exclusivamente
        a <strong>{organization.code}</strong>.
      </p>
      {loading ? <p role="status">Cargando hogar…</p> : null}
      {error ? <p role="alert">{error}</p> : null}
      {success ? <p role="status">{success}</p> : null}
      {rows.map((row) => (
        <CanonicalCard className="entity-review-card" key={row.id}>
          <h3>Declaración vigente de ensayo</h3>
          <dl>
            <dt>Entidad propietaria</dt>
            <dd>{organization.code}</dd>
            <dt>Período</dt>
            <dd>
              {row.periodStart} — {row.periodEnd ?? 'sin fecha final'}
            </dd>
            <dt>Personas declaradas</dt>
            <dd>{row.declaredPersonCount}</dd>
            <dt>Versión independiente</dt>
            <dd>{row.version}</dd>
          </dl>
          <ul>
            {row.members.map((member) => (
              <li key={member.id}>
                {member.givenNames} {member.familyNames}
              </li>
            ))}
          </ul>
        </CanonicalCard>
      ))}
      {organization.permissions.includes('members.profile.household.manage') ? (
        <CanonicalCard className="entity-review-card">
          <h3>{rows.length ? 'Actualizar' : 'Crear'} hogar de ensayo</h3>
          <form onSubmit={(event) => void save(event)}>
            <TextField
              id={'household-period-' + organization.id}
              label="Inicio del período"
              type="date"
              required
              value={periodStart}
              onChange={(event) => setPeriodStart(event.target.value)}
            />
            <TextField
              id={'household-given-' + organization.id}
              label="Nombres del familiar"
              required
              value={givenNames}
              onChange={(event) => setGivenNames(event.target.value)}
            />
            <TextField
              id={'household-family-' + organization.id}
              label="Apellidos del familiar"
              required
              value={familyNames}
              onChange={(event) => setFamilyNames(event.target.value)}
            />
            <TextField
              id={'household-reason-' + organization.id}
              label="Motivo obligatorio (mínimo 10 caracteres)"
              required
              minLength={10}
              maxLength={500}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
            <button
              className="ui-button ui-button--primary"
              disabled={saving || reason.trim().length < 10}
            >
              {saving
                ? 'Guardando…'
                : rows.length
                  ? 'Guardar nueva versión'
                  : 'Crear hogar en ' + organization.code}
            </button>
          </form>
        </CanonicalCard>
      ) : (
        <p>Tu acceso permite consulta, no cambios.</p>
      )}
    </section>
  );
}
